<script setup>
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import BondCompositionChart from '@/components/bonds/BondCompositionChart.vue';
import BondPaymentModal from '@/components/modals/BondPaymentModal.vue';
import ClientBondService from '@/services/ClientBondService';
import {formatAmount, formatDate, formatNumber} from '@/helper';

/**
 * Přehled Dluhopisy klienta podle vzoru (_Docs/IMA-517-dluhopisy-prototyp.html, náhledy 01 a 14).
 *
 *  - Nástěnka klienta (bez clientId, přepínač Investice × Dluhopisy v DashboardClient.vue): sekce pod sebou –
 *    souhrnné hodnoty, rozložení, vlastněné dluhopisy, nejbližší a vyplacené kupóny, pokyny jako karty,
 *    pohyby na účtech a nabídka dluhopisů.
 *  - Detail klienta pro poradce (clientId, karta ClientBondsCard.vue): štítek rámcové smlouvy, souhrnné hodnoty
 *    s rozložením vedle sebe a záložky s tabulkami.
 *
 * Hodnota dluhopisů = nominální hodnota (Q16). Výnosy jsou součty kupónů z plánu výplat (definice čeká na Q15):
 * „Čistý výnos do splatnosti“ = budoucí čisté kupóny, výnos pokynu = kupóny jeho plánu výplat.
 * Data se načítají až při prvním přepnutí na Dluhopisy (komponenta se vykreslí přes v-if).
 */
const props = defineProps({
  // souhrn už načetl rodič (rozhodnutí o přepínači / karta) – nenačítáme ho znovu
  stats: {type: Object, required: true},
  // null = přihlášený klient; jinak ID klienta na jeho detailu (poradce, administrace)
  clientId: {type: [Number, String], default: null},
});

const {t} = useI18n();
const isAdvisor = computed(() => props.clientId !== null && props.clientId !== undefined);
const loading = ref(true);
const composition = ref([]);
const positions = ref([]);
const orders = ref([]);
const upcomingCoupons = ref([]);
const paidCoupons = ref([]);
const cashTransactions = ref([]);
const secTransactions = ref([]);
// pokyn, jehož platební údaje jsou otevřené v modalu
const paymentOrder = ref(null);

// nástěnka klienta: přepínač pohybů a rozbalení delších výpisů („Zobrazit vše“ / „Více informací“)
const transactionsTab = ref('cash');
const expanded = ref({upcoming: false, paid: false, movements: false});
const COLLAPSED_ROWS = 5;
const limited = (rows, key) => (expanded.value[key] ? rows : rows.slice(0, COLLAPSED_ROWS));

// detail klienta pro poradce: záložky s tabulkami
const advisorTab = ref('owned');
const advisorTabs = ['owned', 'upcoming', 'paid', 'orders', 'cash', 'sec'];

/** Barva štítku stavu pokynu. */
const orderStateBadge = {
  AWAITING_ESO: 'bg-primary',
  AWAITING_PAYMENT: 'bg-warning text-dark',
  ACTIVE: 'bg-success',
  CANCELLED: 'bg-secondary',
  TERMINATED: 'bg-secondary',
};

/**
 * Platební údaje má smysl ukázat jen klientovi samotnému u nákupu z aplikace, který ještě není zaplacený
 * (čeká na zpracování nebo na platbu). Poradce na detailu klienta tlačítko nevidí.
 */
function canShowPayment(order) {
  return !isAdvisor.value && order.source === 'APP' && !!order.bond_vs && ['AWAITING_ESO', 'AWAITING_PAYMENT'].includes(order.state);
}

/** Pokyn, který ještě není aktivní: místo výnosu se ukazuje datum zadání (a případně platební údaje). */
const isPendingOrder = order => ['AWAITING_ESO', 'AWAITING_PAYMENT'].includes(order.state);
const orderCurrency = order => order.currency ?? order.bond?.currency ?? 'CZK';
const orderNominal = order => (Number(order.quantity) || 0) * (Number(order.nominal_value) || 0);
/** Hodnota vč. AÚV: u nákupu z aplikace celková cena, u nákupu z ESO (AÚV neznáme) nominál. */
const orderTotal = order => order.total_amount ?? (orderNominal(order) + (Number(order.accrued_interest) || 0));
const amountOrDash = (value, currency) => (value === null || value === undefined ? '---' : formatAmount(value, currency));

const ownedTotal = computed(() => positions.value.reduce((sum, position) => sum + (Number(position.nominal_amount) || 0), 0));
const ownedCurrency = computed(() => positions.value[0]?.currency ?? props.stats.currency ?? 'CZK');

/**
 * Štítek rámcové smlouvy pro dluhopisy u poradce – jen u podepsané smlouvy. Nový klient bez podepsané dokumentace
 * nevidí nic (stav „čeká na podpis“ / „chybí“ se na kartě neukazuje, rámcová smlouva se podepíše s nákupem).
 */
const frameworkBadge = computed(() => {
  if (props.stats.framework_contract_state !== 'SIGNED') {
    return null;
  }
  return {
    class: 'bg-success',
    text: props.stats.framework_contract_signed_date
      ? t('clientBonds.framework_signed', {date: formatDate(props.stats.framework_contract_signed_date, 'd.m.Y')})
      : t('clientBonds.framework_signed_no_date'),
  };
});

/** Řádky tabulek u poradce (vue-good-table bez mode="remote" – řazení a stránkování v tabulce). */
const advisorTables = computed(() => ({
  owned: {
    columns: [
      {label: t('clientBonds.bond'), field: 'name'},
      {label: 'ISIN', field: 'isin'},
      {label: t('clientBonds.coupon_payout'), field: 'frequency', sortable: false},
      {label: t('clientBonds.issue'), field: 'issue_date'},
      {label: t('clientBonds.maturity'), field: 'maturity_date'},
      {label: t('clientBonds.count'), field: 'quantity', type: 'number'},
      {label: t('clientBonds.nominal_volume'), field: 'nominal_amount', type: 'number'},
    ],
    rows: positions.value.map(position => ({
      name: position.bond.name,
      isin: position.bond.isin,
      frequency: position.bond.coupon_frequency_label,
      issue_date: position.bond.issue_date,
      maturity_date: position.bond.maturity_date,
      quantity: Number(position.quantity),
      nominal_amount: Number(position.nominal_amount),
      currency: position.currency,
    })),
  },
  upcoming: couponTable(upcomingCoupons.value),
  paid: couponTable(paidCoupons.value),
  orders: {
    columns: [
      {label: t('clientBonds.order_number'), field: 'bond_vs'},
      {label: t('clientBonds.bond'), field: 'name'},
      {label: t('clientBonds.pieces'), field: 'quantity', type: 'number'},
      {label: t('clientBonds.nominal'), field: 'nominal', type: 'number'},
      {label: t('clientBonds.accrued'), field: 'accrued_interest', type: 'number'},
      {label: t('clientBonds.value_with_accrued'), field: 'total', type: 'number'},
      {label: t('clientBonds.ordered_at'), field: 'signed_date'},
      {label: t('clientBonds.state'), field: 'state', sortable: false},
    ],
    rows: orders.value.map(order => ({
      bond_vs: order.bond_vs,
      name: order.bond?.name,
      quantity: Number(order.quantity),
      nominal: orderNominal(order),
      accrued_interest: order.accrued_interest,
      total: orderTotal(order),
      signed_date: order.signed_date,
      state: order.state,
      currency: orderCurrency(order),
    })),
  },
  cash: {
    columns: [
      {label: t('clientBonds.date'), field: 'transaction_date'},
      {label: t('clientBonds.description'), field: 'description'},
      {label: t('clientBonds.reference'), field: 'trans_ref'},
      {label: t('clientBonds.amount'), field: 'amount', type: 'number'},
    ],
    rows: cashTransactions.value.map(transaction => ({
      transaction_date: transaction.transaction_date,
      description: transaction.type?.cash_transaction_type,
      trans_ref: transaction.trans_ref,
      amount: Number(transaction.amount),
      currency: transaction.currency,
    })),
  },
  sec: {
    columns: [
      {label: t('clientBonds.date'), field: 'transaction_date'},
      {label: t('clientBonds.bond'), field: 'name'},
      {label: 'ISIN', field: 'isin'},
      {label: t('clientBonds.pieces'), field: 'quantity', type: 'number'},
      {label: t('clientBonds.nominal'), field: 'amount', type: 'number'},
    ],
    rows: secTransactions.value.map(transaction => ({
      transaction_date: transaction.transaction_date,
      name: transaction.sec_account?.equity_name,
      isin: transaction.sec_account?.isin,
      quantity: Number(transaction.quantity),
      amount: Number(transaction.amount),
      currency: transaction.currency,
    })),
  },
}));

function couponTable(coupons) {
  return {
    columns: [
      {label: t('clientBonds.date'), field: 'payment_date'},
      {label: t('clientBonds.bond'), field: 'name'},
      {label: 'ISIN', field: 'isin'},
      {label: t('clientBonds.gross_yield'), field: 'amount_gross', type: 'number'},
      {label: t('clientBonds.net_yield'), field: 'amount_net', type: 'number'},
    ],
    rows: coupons.map(coupon => ({
      payment_date: coupon.payment_date,
      name: coupon.bond?.name,
      isin: coupon.bond?.isin,
      amount_gross: Number(coupon.amount_gross),
      amount_net: Number(coupon.amount_net),
      currency: coupon.currency,
    })),
  };
}

/** Formát buňky tabulky u poradce: data, částky v měně řádku, kusy. */
function advisorCell(field, row) {
  const value = row[field];
  if (value === null || value === undefined || value === '') {
    return '---';
  }
  if (['issue_date', 'maturity_date', 'payment_date', 'signed_date', 'transaction_date'].includes(field)) {
    return formatDate(value, 'd.m.Y');
  }
  if (['nominal_amount', 'amount_gross', 'amount_net', 'nominal', 'accrued_interest', 'total', 'amount'].includes(field)) {
    return formatAmount(value, row.currency ?? 'CZK');
  }
  if (field === 'quantity') {
    return formatNumber(value, 0) + ' ks';
  }
  return value;
}

onMounted(async () => {
  // klient bez dluhopisů vidí jen nabídku, ostatní přehledy by byly prázdné
  if (!props.stats.has_bonds) {
    loading.value = false;
    return;
  }
  // každý přehled zvlášť – chyba jednoho požadavku nesmí nechat celou kartu ve stavu načítání,
  // nenačtený přehled zůstane prázdný (allSettled místo all)
  const results = await Promise.allSettled([
    ClientBondService.getComposition(props.clientId),
    ClientBondService.getPositions(props.clientId),
    ClientBondService.getOrders(props.clientId),
    ClientBondService.getUpcomingCoupons(props.clientId),
    ClientBondService.getPaidCoupons(props.clientId),
    ClientBondService.getCashTransactions(props.clientId),
    ClientBondService.getSecTransactions(props.clientId),
  ]);
  const [cmp, pos, ord, upcoming, paid, cash, sec] = results.map(result => (result.status === 'fulfilled' && Array.isArray(result.value) ? result.value : []));
  composition.value = cmp;
  positions.value = pos;
  orders.value = ord;
  upcomingCoupons.value = upcoming;
  paidCoupons.value = paid;
  cashTransactions.value = cash;
  secTransactions.value = sec;
  loading.value = false;
});

/**
 * Věta o počtu dostupných dluhopisů s českým/slovenským skloňováním: 1 dluhopis, 2–4 dluhopisy, 0 a 5+ dluhopisů.
 * (i18n v aplikaci nemá pravidla množného čísla pro cs/sk, proto výběr tvaru tady)
 */
const offerAvailableText = computed(() => {
  const count = Number(props.stats.available_count) || 0;
  const form = count === 1 ? 'one' : (count >= 2 && count <= 4 ? 'few' : 'many');
  return t('clientBonds.offer_available_' + form, {count});
});

/** Rozložení má smysl jen u aktivních držeb – klient s pokyny, ale bez vlastněných dluhopisů, graf nemá. */
const hasComposition = computed(() => composition.value.length > 0);

/** Souhrnné hodnoty (stejné u klienta i poradce). */
const bigValues = computed(() => [
  {label: t('clientBonds.bonds_value'), value: formatAmount(props.stats.nominal_value, props.stats.currency)},
  {label: t('clientBonds.net_yield_to_maturity'), value: formatAmount(props.stats.coupons_upcoming_net, props.stats.currency)},
  {label: t('clientBonds.coupons_paid_net'), value: formatAmount(props.stats.coupons_paid_net, props.stats.currency)},
  {label: t('clientBonds.next_coupon_payout'), value: props.stats.next_coupon ? formatDate(props.stats.next_coupon.payment_date, 'd.m.Y') : '---'},
]);
</script>

<template>
  <!-- ===================== detail klienta pro poradce ===================== -->
  <div v-if="isAdvisor">
    <div v-if="frameworkBadge" class="mb-3"><span class="badge" :class="frameworkBadge.class">{{ frameworkBadge.text }}</span></div>
    <div v-if="!stats.has_bonds" class="text-muted">{{ t('clientBonds.client_has_no_bonds') }}</div>
    <template v-else>
      <div class="row align-items-center mb-4">
        <div class="col-12 col-lg-6">
          <div class="row g-2">
            <div v-for="item of bigValues" :key="item.label" class="col-6">
              <div class="card card--flat card--big-value h-100">
                <div class="card-body">
                  <div class="card__value__label">{{ item.label }}</div>
                  <div class="card__value">{{ item.value }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <BondCompositionChart v-if="!loading && hasComposition" :items="composition"/>
          <!-- bez aktivních držeb (jen pokyny) by místo grafu zůstalo prázdné místo -->
          <div v-else-if="!loading" class="text-center text-muted py-4">{{ t('clientBonds.no_composition') }}</div>
        </div>
      </div>

      <ul class="nav--tabs nav nav--pills mb-3" role="tablist">
        <li v-for="tab of advisorTabs" :key="tab" class="nav-item" role="presentation">
          <button type="button" class="nav-link" :class="{active: advisorTab === tab}" role="tab" :aria-selected="advisorTab === tab" @click="advisorTab = tab">{{ t('clientBonds.tab_' + tab) }}</button>
        </li>
      </ul>
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border text-primary" role="status"><span class="visually-hidden">{{ t('clientBonds.loading') }}</span></div>
      </div>
      <vue-good-table v-else :key="advisorTab" :columns="advisorTables[advisorTab].columns" :rows="advisorTables[advisorTab].rows"
                      :sort-options="{enabled: true}"
                      :pagination-options="{enabled: true, perPageDropdown: [10, 20], rowsPerPageLabel: t('strategies.entriesPerPage'), nextLabel: t('strategies.next'), prevLabel: t('strategies.previous'), ofLabel: t('strategies.of')}">
        <template #table-row="props">
          <span v-if="props.column.field === 'state'" class="badge" :class="orderStateBadge[props.row.state]">{{ t('bondsDetail.client_bond_states.' + props.row.state) }}</span>
          <span v-else-if="props.column.field === 'amount' && advisorTab === 'cash'" :class="props.row.amount < 0 ? 'text-danger' : 'text-success'">{{ advisorCell('amount', props.row) }}</span>
          <span v-else>{{ advisorCell(props.column.field, props.row) }}</span>
        </template>
        <template v-slot:emptystate>
          <div class="text-center">{{ t('clientBonds.no_records') }}</div>
        </template>
      </vue-good-table>
    </template>
  </div>

  <!-- ===================== nástěnka klienta ===================== -->
  <div v-else>
    <!-- klient bez dluhopisů: jen odkaz na nabídku -->
    <div v-if="!stats.has_bonds" class="page-section">
      <div class="page-section__heading"><div class="page-section__title">{{ t('clientBonds.my_bonds') }}</div></div>
      <div class="page-section__content">
        <div class="card">
          <div class="card-body d-flex flex-wrap gap-3 align-items-center justify-content-between">
            <div>
              <div class="card-header__title">{{ t('clientBonds.offer_title') }}</div>
              <div class="text-muted">{{ t('clientBonds.no_bonds_yet') }}</div>
            </div>
            <router-link :to="{name: 'ClientBonds'}" class="btn btn-primary">{{ t('clientBonds.show_offer') }}</router-link>
          </div>
        </div>
      </div>
    </div>

    <template v-else>
      <div class="page-section">
        <div class="page-section__heading"><div class="page-section__title">{{ t('clientBonds.my_bonds') }}</div></div>
        <div class="page-section__content">
          <div class="row g-2 g-md-3">
            <div v-for="item of bigValues" :key="item.label" class="col-12 col-md-6 col-lg-3">
              <div class="card card--flat card--big-value h-100">
                <div class="card-body">
                  <div class="card__value__label">{{ item.label }}</div>
                  <div class="card__value">{{ item.value }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border text-primary" role="status"><span class="visually-hidden">{{ t('clientBonds.loading') }}</span></div>
      </div>

      <template v-else>
        <!-- rozložení jen u aktivních držeb – klient s pokyny, ale bez vlastněných dluhopisů, by měl prázdný graf -->
        <div v-if="hasComposition" class="page-section mt-5">
          <div class="page-section__heading"><div class="page-section__title">{{ t('clientBonds.composition') }}</div></div>
          <div class="page-section__content">
            <div class="card">
              <div class="card-body card-body--only-chart">
                <BondCompositionChart :items="composition"/>
              </div>
            </div>
          </div>
        </div>

        <div class="page-section mt-5">
          <div class="page-section__heading"><div class="page-section__title">{{ t('clientBonds.owned_overview') }}</div></div>
          <div class="page-section__content">
            <div class="card shadow">
              <div class="card-body">
                <div class="table-container">
                  <table class="table table--last-to-right table--cells-py-0">
                    <thead>
                    <tr>
                      <th>{{ t('clientBonds.bond') }}<div class="table__sub-value">ISIN</div></th>
                      <th>{{ t('clientBonds.issue') }}<div class="table__sub-value">{{ t('clientBonds.maturity') }}</div></th>
                      <th>{{ t('clientBonds.coupon_payout') }}</th>
                      <th>{{ t('clientBonds.count') }}</th>
                      <th>{{ t('clientBonds.nominal_volume') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="position of positions" :key="position.bond.id">
                      <td>
                        <router-link :to="{name: 'ClientBondDetail', params: {id: position.bond.id}}">{{ position.bond.name }}</router-link>
                        <div class="table__sub-value">{{ position.bond.isin }}</div>
                      </td>
                      <td>
                        {{ position.bond.issue_date ? formatDate(position.bond.issue_date, 'd.m.Y') : '---' }}
                        <div class="table__sub-value">{{ position.bond.maturity_date ? formatDate(position.bond.maturity_date, 'd.m.Y') : '---' }}</div>
                      </td>
                      <td>{{ position.bond.coupon_frequency_label ?? '---' }}</td>
                      <td>{{ formatNumber(position.quantity, 0) }} ks</td>
                      <td>{{ formatAmount(position.nominal_amount, position.currency) }}</td>
                    </tr>
                    <tr v-if="!positions.length">
                      <td colspan="5" class="table__empty-td"><div class="table__empty-text">{{ t('clientBonds.no_owned') }}</div></td>
                    </tr>
                    <!-- celkový nominální objem vlastněných dluhopisů -->
                    <tr v-else>
                      <td colspan="4"></td>
                      <td class="fw-bold">{{ formatAmount(ownedTotal, ownedCurrency) }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- nejbližší a vyplacené kupóny – stejná tabulka, 5 řádků a „Zobrazit vše“ -->
        <div v-for="couponSection of [{key: 'upcoming', title: 'upcoming_coupons', rows: upcomingCoupons}, {key: 'paid', title: 'paid_coupons', rows: paidCoupons}]" :key="couponSection.key" class="page-section mt-5">
          <div class="page-section__heading"><div class="page-section__title">{{ t('clientBonds.' + couponSection.title) }}</div></div>
          <div class="page-section__content">
            <div class="card shadow">
              <div class="card-body">
                <div class="table-container">
                  <table class="table table--last-to-right table--cells-py-0">
                    <thead>
                    <tr>
                      <th>{{ t('clientBonds.date') }}</th>
                      <th>{{ t('clientBonds.bond') }}<div class="table__sub-value">ISIN</div></th>
                      <th>{{ t('clientBonds.gross_yield') }}<div class="table__sub-value">{{ t('clientBonds.net_yield') }}</div></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(coupon, index) of limited(couponSection.rows, couponSection.key)" :key="couponSection.key + index">
                      <td>{{ formatDate(coupon.payment_date, 'd.m.Y') }}</td>
                      <td>
                        {{ coupon.bond?.name ?? '---' }}
                        <div class="table__sub-value">{{ coupon.bond?.isin }}</div>
                      </td>
                      <td>
                        {{ formatAmount(coupon.amount_gross, coupon.currency) }}
                        <div class="table__sub-value">{{ formatAmount(coupon.amount_net, coupon.currency) }}</div>
                      </td>
                    </tr>
                    <tr v-if="!couponSection.rows.length">
                      <td colspan="3" class="table__empty-td"><div class="table__empty-text">{{ t('clientBonds.no_coupons') }}</div></td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="couponSection.rows.length > COLLAPSED_ROWS" class="text-center mt-2">
                  <button type="button" class="btn btn-light" @click="expanded[couponSection.key] = !expanded[couponSection.key]">{{ expanded[couponSection.key] ? t('clientBonds.show_less') : t('clientBonds.show_all') }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- pokyny jako karty po třech -->
        <div class="page-section mt-5">
          <div class="page-section__heading"><div class="page-section__title">{{ t('clientBonds.orders') }}</div></div>
          <div class="page-section__content">
            <div class="row g-3">
              <div v-for="order of orders" :key="order.id" class="col-12 col-md-6 col-xl-4">
                <div class="card h-100 bond-order-card">
                  <div class="card-header">
                    <div class="d-flex justify-content-between align-items-start gap-2">
                      <div class="card-header__title bond-order-card__title" :title="order.bond?.name">{{ order.bond?.name ?? '---' }}</div>
                      <span class="badge flex-shrink-0" :class="orderStateBadge[order.state]">{{ t('bondsDetail.client_bond_states.' + order.state) }}</span>
                    </div>
                    <div class="card-header__subtitle">{{ t('clientBonds.order_no', {no: order.bond_vs ?? '---'}) }}</div>
                  </div>
                  <div class="card-body d-flex flex-column">
                    <dl class="row mb-0 small">
                      <dt class="col-6 fw-normal text-muted">{{ t('clientBonds.price_per_piece') }}:</dt><dd class="col-6 mb-1">{{ formatAmount(order.nominal_value, orderCurrency(order)) }}</dd>
                      <dt class="col-6 fw-normal text-muted">{{ t('clientBonds.bond_yield') }}:</dt><dd class="col-6 mb-1">{{ order.bond?.coupon_rate === null || order.bond?.coupon_rate === undefined ? '---' : formatNumber(order.bond.coupon_rate) + ' % p.a.' }}</dd>
                    </dl>
                    <hr>
                    <dl class="row mb-0 small">
                      <dt class="col-6 fw-normal text-muted">{{ t('clientBonds.pieces_count') }}:</dt><dd class="col-6 mb-1">{{ formatNumber(order.quantity ?? 0, 0) }} ks</dd>
                      <dt class="col-6 fw-normal text-muted">{{ t('clientBonds.nominal') }}:</dt><dd class="col-6 mb-1">{{ formatAmount(orderNominal(order), orderCurrency(order)) }}</dd>
                      <dt class="col-6 fw-normal text-muted">{{ t('clientBonds.accrued') }}:</dt><dd class="col-6 mb-1">{{ amountOrDash(order.accrued_interest, orderCurrency(order)) }}</dd>
                      <dt class="col-6 fw-normal text-muted">{{ t('clientBonds.value_with_accrued') }}:</dt><dd class="col-6 mb-1">{{ formatAmount(orderTotal(order), orderCurrency(order)) }}</dd>
                      <dt class="col-6 fw-normal text-muted">{{ isPendingOrder(order) ? t('clientBonds.ordered_at') : t('clientBonds.bought_at') }}:</dt>
                      <dd class="col-6 mb-1">{{ (isPendingOrder(order) ? order.signed_date : (order.activation_date ?? order.signed_date)) ? formatDate(isPendingOrder(order) ? order.signed_date : (order.activation_date ?? order.signed_date), 'd.m.Y') : '---' }}</dd>
                    </dl>
                    <hr class="mt-auto">
                    <!-- spodní část má ve všech kartách stejnou výšku: platební údaje (nezaplacený pokyn z aplikace),
                         důvod storna (zrušený pokyn – výnosy by byly jen pomlčky), jinak čistý a hrubý výnos z plánu výplat -->
                    <div class="bond-order-card__footer">
                      <button v-if="canShowPayment(order)" type="button" class="btn btn-outline-tenant w-100" @click="paymentOrder = order">{{ t('clientBonds.payment_qr') }}</button>
                      <div v-else-if="order.state === 'CANCELLED'" class="small text-muted">
                        <template v-if="order.reservation_changed_at">{{ formatDate(order.reservation_changed_at, 'd.m.Y') }}</template>
                        <template v-if="order.reservation_changed_at && order.reservation_note"> · </template>
                        <template v-if="order.reservation_note">{{ order.reservation_note }}</template>
                      </div>
                      <dl v-else class="row mb-0 small w-100">
                        <dt class="col-6 fw-normal text-muted">{{ t('clientBonds.net_yield') }}:</dt><dd class="col-6 mb-1">{{ amountOrDash(order.coupons_net, orderCurrency(order)) }}</dd>
                        <dt class="col-6 fw-normal text-muted">{{ t('clientBonds.gross_yield') }}:</dt><dd class="col-6 mb-1">{{ amountOrDash(order.coupons_gross, orderCurrency(order)) }}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="!orders.length" class="col-12">
                <div class="card"><div class="card-body text-center text-muted">{{ t('clientBonds.no_orders') }}</div></div>
              </div>
            </div>
          </div>
        </div>

        <!-- pohyby na účtech strategie Dluhopisy -->
        <div class="page-section mt-5">
          <div class="page-section__heading"><div class="page-section__title">{{ t('clientBonds.movements') }}</div></div>
          <div class="page-section__content">
            <ul class="nav--tabs nav nav--pills mb-3" role="tablist">
              <li class="nav-item col-6" role="presentation">
                <button type="button" class="nav-link" :class="{active: transactionsTab === 'cash'}" role="tab" @click="transactionsTab = 'cash'">{{ t('clientBonds.cash_movements') }}</button>
              </li>
              <li class="nav-item col-6" role="presentation">
                <button type="button" class="nav-link" :class="{active: transactionsTab === 'sec'}" role="tab" @click="transactionsTab = 'sec'">{{ t('clientBonds.sec_movements') }}</button>
              </li>
            </ul>
            <div class="card shadow">
              <div class="card-body">
                <div class="table-container">
                  <table v-if="transactionsTab === 'cash'" class="table table--last-to-right table--cells-py-0">
                    <thead>
                    <tr>
                      <th>{{ t('clientBonds.date') }}<div class="table__sub-value">{{ t('clientBonds.reference') }}</div></th>
                      <th>{{ t('clientBonds.description') }}<div class="table__sub-value">{{ t('clientBonds.instrument') }}</div></th>
                      <th>{{ t('clientBonds.amount') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="transaction of limited(cashTransactions, 'movements')" :key="transaction.id">
                      <td>{{ formatDate(transaction.transaction_date, 'd.m.Y') }}<div class="table__sub-value">{{ transaction.trans_ref }}</div></td>
                      <td>{{ transaction.type?.cash_transaction_type ?? '---' }}<div class="table__sub-value">{{ t('clientBonds.instrument_bond') }}</div></td>
                      <td :class="Number(transaction.amount) < 0 ? 'text-danger' : 'text-success'">{{ formatAmount(transaction.amount, transaction.currency) }}</td>
                    </tr>
                    <tr v-if="!cashTransactions.length">
                      <td colspan="3" class="table__empty-td"><div class="table__empty-text">{{ t('clientBonds.no_transactions') }}</div></td>
                    </tr>
                    </tbody>
                  </table>
                  <table v-else class="table table--last-to-right table--cells-py-0">
                    <thead>
                    <tr>
                      <th>{{ t('clientBonds.date') }}</th>
                      <th>{{ t('clientBonds.bond') }}<div class="table__sub-value">ISIN</div></th>
                      <th>{{ t('clientBonds.nominal') }}<div class="table__sub-value">{{ t('clientBonds.pieces') }}</div></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="transaction of limited(secTransactions, 'movements')" :key="transaction.id">
                      <td>{{ formatDate(transaction.transaction_date, 'd.m.Y') }}</td>
                      <td>{{ transaction.sec_account?.equity_name ?? '---' }}<div class="table__sub-value">{{ transaction.sec_account?.isin }}</div></td>
                      <td>{{ formatAmount(transaction.amount, transaction.currency, 2, true) }}<div class="table__sub-value">{{ formatNumber(transaction.quantity, 0, true) }} ks</div></td>
                    </tr>
                    <tr v-if="!secTransactions.length">
                      <td colspan="3" class="table__empty-td"><div class="table__empty-text">{{ t('clientBonds.no_transactions') }}</div></td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="(transactionsTab === 'cash' ? cashTransactions : secTransactions).length > COLLAPSED_ROWS" class="text-center mt-2">
                  <button type="button" class="btn btn-light" @click="expanded.movements = !expanded.movements">{{ expanded.movements ? t('clientBonds.show_less') : t('clientBonds.more_info') }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- nabídka dluhopisů pro profil klienta -->
      <div v-if="stats.available_count" class="card mt-5">
        <div class="card-body d-flex flex-wrap gap-3 align-items-center justify-content-between">
          <div>
            <div class="card-header__title">{{ t('clientBonds.offer_title') }}</div>
            <div class="text-muted small">{{ offerAvailableText }}</div>
          </div>
          <router-link :to="{name: 'ClientBonds'}" class="btn btn-primary">{{ t('clientBonds.show_bonds') }}</router-link>
        </div>
      </div>
    </template>
    <BondPaymentModal v-if="paymentOrder" :order="paymentOrder" @close="paymentOrder = null"/>
  </div>
</template>

<style scoped>
/* Název dluhopisu vždy na dva řádky (delší se zkrátí), aby údaje pod hlavičkou začínaly ve všech kartách stejně. */
.bond-order-card__title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
  min-height: 2.6em;
}

/* Spodní část karty – stejná výška pro tlačítko, důvod storna i dva řádky výnosů. */
.bond-order-card__footer {
  display: flex;
  align-items: center;
  min-height: 3.25rem;
}
</style>
